import { getUserLang, isEuEnv } from '@mb/lib';
import { sendViewSearchResultsAnalyticsEvent } from '@mb/lib/analytics';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { Customer } from 'code/MaterialBank/ReactComponents/view/frontend/web/js/mb-frontend/src/entities/customer';
import orderBy from 'lodash/orderBy';
import { ApiRawMaterial, FilterData } from '../../../pages/ProductSearch/types';
import {
  checkStockAvailability,
  transformMaterialToAppFormat,
} from '../helpers';
import { getUserAttributes, extractPhotoExtension } from '../helpers';
import { bridgeSettings } from '../HotspotMainWrapper/config';
import {
  hotspotPositionSelector,
  hotspotQuerySelector,
  hotspotSelectedColorsSelector,
  hotspotSelectedLabelsSelector,
} from '../redux/selectors/hotspotSelector';
import {
  setColors,
  setLabels,
  setResults,
  setIsSearching,
  setSelectedColors,
  setQuery,
  setSelectedLabels,
  setXRequestId,
} from '../redux/sliceCreators/hotspotSlice';
import { AppDispatch, RootState } from '../redux/store';
import { CropData, FilterLabels } from '../types';

interface PayloadBody {
  crop?: CropData;
  similarityText?: string;
  colors?: string[];
  imageUrl?: string;
  isNewSpot?: boolean;
  isInit?: boolean;
  labels?: string[];
}

const extraFieldsList = [
  'configurations',
  'components',
  'family_id',
  'taxonomy',
  'ss_facet_color_filter',
  'light_reflectance_value',
  'sample_note',
  'is_digital',
];

export const getMaterialsByHotspot =
  (payload: PayloadBody) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();
      const reduxCrop = hotspotPositionSelector(state);
      const reduxColors = hotspotSelectedColorsSelector(state);
      const reduxLabels = hotspotSelectedLabelsSelector(state);
      const reduxHotspotQuery = hotspotQuerySelector(state);
      const {
        similarityText = reduxHotspotQuery,
        crop = reduxCrop,
        imageUrl,
        colors = reduxColors,
        labels = reduxLabels,
        isNewSpot,
        isInit,
      } = payload;
      const userData = getUserAttributes();
      const payloadColors = colors;
      const configLang =
        typeof ENVIRONMENT_REGION !== 'undefined' ? ENVIRONMENT_REGION : 'US';
      const userLang = getUserLang();
      let searchCore: Record<string, unknown> = {};
      if ((similarityText || '')?.length === 0 || isNewSpot) {
        searchCore = { crop };
      } else {
        searchCore = { similarityText };
      }
      let colorCondition = {};
      if ((payloadColors || []).length > 0) {
        colorCondition = {
          'filter.virtual_color_group': payloadColors,
          'bgfilter.visibility': 'Catalog',
        };
      } else {
        colorCondition = { 'bgfilter.visibility': 'Catalog| Search' };
      }
      if (Array.isArray(labels) && labels?.length > 0 && !isNewSpot) {
        searchCore['filter.content_textile'] = labels;
      }

      const bgfilter = getBgFilter(userData || {});

      const body = {
        siteId: bridgeSettings.siteId,
        lang: `${userLang}-${configLang}`,
        resultsPerPage: 9,
        page: 1,
        q: '',
        fieldsOnly: false,
        extraFields: extraFieldsList,
        similarityUrl: imageUrl,
        customerEmail: userData?.email,
        customerId: Number(userData?.customer_id),
        ...bgfilter,
        ...searchCore,
        ...colorCondition,
      };
      if (isNewSpot) {
        dispatch(setSelectedColors([]));
        dispatch(setSelectedLabels([]));
        dispatch(setQuery(''));
      }
      if (isInit) {
        dispatch(setIsSearching(true));
      }
      const data = await fetch(bridgeSettings.siteUrl, {
        method: 'POST',
        body: JSON.stringify(body),
      }).then((res) => {
        const xRequestId = res.headers.get('x-request-id');
        if (xRequestId != null) {
          dispatch(setXRequestId(xRequestId));
        }
        return res.json();
      });
      const imageExtension = imageUrl ? extractPhotoExtension(imageUrl) : 'jpg';
      sendViewSearchResultsAnalyticsEvent({
        query: imageExtension,
        searchType: 'visual_search',
        resultsNumber: data?.pagination?.totalResults || 0,
      });
      let results = [];
      if (Array.isArray(data?.results)) {
        results = data.results.map((item: ApiRawMaterial) =>
          transformMaterialToAppFormat(item),
        );
      }
      dispatch(setResults(results));
      const resultsWithStockData = await checkStockAvailability(results);
      if ((resultsWithStockData || []).length > 0) {
        dispatch(setResults(resultsWithStockData));
      }

      const filtersGroup = data?.facets?.find(
        (item: FilterData) => item.field === FilterLabels.TAXONOMY,
      );
      const colorsGroup = data?.facets?.find(
        (item: FilterData) => item.field === FilterLabels.COLOR,
      );

      if (Array.isArray(filtersGroup?.values)) {
        const orderedFilters = orderBy(
          filtersGroup?.values,
          ['count'],
          ['desc'],
        );
        dispatch(setLabels(orderedFilters || []));
      }

      if (Array.isArray(colorsGroup?.values)) {
        const orderedColors = orderBy(colorsGroup?.values, ['count'], ['desc']);
        dispatch(setColors(orderedColors || []));
      }

      dispatch(setIsSearching(false));
    } catch (e) {
      dispatch(setIsSearching(false));
      dispatch(setResults([]));
      handleSystemErrorReport(
        `getMaterialsByHotspot: ${e.message}`,
        'getMaterials',
      );
    }
  };

////
function getBgFilter(customer: Partial<Customer>) {
  const bgfilter: Record<string, string> = {};
  if (!isEuEnv) {
    bgfilter['state_permission'] = customer?.state || '';
  }

  if (customer?.country) {
    bgfilter['country_permission'] = customer?.country || '';
  }

  return { bgfilter };
}
